import {CommonModule} from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {
    MONITORING_TYPE_DISPLAY_ORDER,
    VehicleMonitoringTypeOption,
    VEHICLE_MONITORING_TYPES,
} from '../vehicle-monitoring/vehicle-monitoring.constants';

@Component({
    selector: 'app-monitoring-type-multiselect',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './monitoring-type-multiselect.component.html',
    styleUrl: './monitoring-type-multiselect.component.css',
})
export class MonitoringTypeMultiselectComponent {
    @Input() selectedValues: string[] = [];
    @Input() options: VehicleMonitoringTypeOption[] = VEHICLE_MONITORING_TYPES;
    @Input() orderedValues: string[] | null = null;
    @Input() allSelectedLabel = 'All Monitoring Types';
    @Output() selectedValuesChange = new EventEmitter<string[]>();
    @Output() selectionApplied = new EventEmitter<string[]>();

    @ViewChild('dropdownRoot') dropdownRoot?: ElementRef<HTMLElement>;

    isOpen = false;

    private readonly checkboxSelector = '.vm-monitoring-checkbox';
    private readonly triggerSelector = '.vm-monitoring-trigger';

    constructor(private elementRef: ElementRef<HTMLElement>) {}

    get displayOrder(): string[] {
        return this.orderedValues ?? MONITORING_TYPE_DISPLAY_ORDER;
    }

    get collapsedLabel(): string {
        const selectedCount = this.selectedValues.length;
        const totalCount = this.displayOrder.length;

        if (selectedCount === 0 || selectedCount === totalCount) {
            return this.allSelectedLabel;
        }

        const orderedSelected = this.displayOrder.filter(type =>
            this.selectedValues.includes(type),
        );
        const labels = orderedSelected.map(code =>
            this.options.find(item => item.value === code)?.label ?? code,
        );

        if (selectedCount === 1) {
            return labels[0];
        }

        if (selectedCount === 2) {
            return `${labels[0]}, ${labels[1]}`;
        }

        return `${labels[0]} (+${selectedCount - 1})`;
    }

    isSelected(value: string): boolean {
        return this.selectedValues.includes(value);
    }

    toggleOpen(event?: Event): void {
        event?.stopPropagation();
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            queueMicrotask(() => this.focusFirstOption());
        }
    }

    closePanel(): void {
        this.isOpen = false;
    }

    toggleOption(value: string, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        const next = checked
            ? [...this.selectedValues, value]
            : this.selectedValues.filter(item => item !== value);
        this.selectedValues = next;
        this.selectedValuesChange.emit(next);
        this.selectionApplied.emit(next);
    }

    onTriggerKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault();
            if (!this.isOpen) {
                this.isOpen = true;
                queueMicrotask(() => this.focusFirstOption());
            }
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.closePanel();
        }
    }

    onOptionKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.closePanel();
            this.focusTrigger();
            return;
        }

        const checkboxes = this.getOptionCheckboxes();
        const currentIndex = checkboxes.findIndex(checkbox => checkbox === event.target);
        if (currentIndex < 0) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            checkboxes[Math.min(currentIndex + 1, checkboxes.length - 1)]?.focus();
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (currentIndex === 0) {
                this.closePanel();
                this.focusTrigger();
                return;
            }
            checkboxes[currentIndex - 1]?.focus();
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target as Node)) {
            this.closePanel();
        }
    }

    private getOptionCheckboxes(): HTMLInputElement[] {
        const root = this.dropdownRoot?.nativeElement ?? this.elementRef.nativeElement;
        return Array.from(root.querySelectorAll<HTMLInputElement>(this.checkboxSelector));
    }

    private focusFirstOption(): void {
        this.getOptionCheckboxes()[0]?.focus();
    }

    private focusTrigger(): void {
        const root = this.dropdownRoot?.nativeElement ?? this.elementRef.nativeElement;
        root.querySelector<HTMLButtonElement>(this.triggerSelector)?.focus();
    }
}
